import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { columnDefs, defaultColDef } from './definitions-config';

import {
  getProjectSubmissionList,
  updateProjectStatus,
  assignEvaluatorInProject,
  getEvaluatorListForProjectSubmission,
  getProjectNotAcceptReason,
  fetchAllProjects,
  fetchAllProjectCategories,
} from '../../../services/apiService';

const StatusDropdownContext = React.createContext();
const EvaluatorDropdownContext = React.createContext();

function ProjectSubmissionTable({
  goalType,
  refreshCounter,
  isApplyDefaultFilter,
  setIsApplyDefaultFilter,
}) {
  const [gridApi, setGridApi] = React.useState(null);

  const pageSize = 10;

  const [evaluatorList, setEvaluatorList] = React.useState([]);
  const [notAcceptReasonList, setNotAcceptReasonList] = React.useState([]);

  const [isFirstTimeFilter, setIsFirstTimeFilter] = React.useState(true);

  const [allProjectList, setAllProjectList] = React.useState([]);
  const [projectCategoryList, setProjectCategoryList] = React.useState([]);

  const getProjectFilterList = (selectedList, dataList) => {
    if (selectedList && dataList && dataList.length) {
      const res = selectedList.map((item) => {
        const index = dataList.findIndex((itm) => itm.name === item);
        if (index < 0) return null;
        return dataList[index].id;
      });
      if (res.indexOf(null) >= 0) return null;
      return res;
    }
    return null;
  };

  const getProjectCategoryFilterList = (selectedList, dataList) => {
    if (selectedList && dataList && dataList.length) {
      let projIdList = [];
      selectedList.forEach((item) => {
        const index = dataList.findIndex((itm) => itm.name === item);
        const projIdArr = dataList[index].projectIds;
        projIdList = [...projIdList, ...projIdArr];
      });
      return projIdList;
    }
    return null;
  };

  const getServerSideDatasource = (
    mGoalType,
    mAllProjectList,
    mProjectCategoryList
  ) => {
    return {
      getRows: (params) => {
        const reevaluationRequestedFn = () => {
          if (params.request?.filterModel?.revaluation?.values) {
            if (params.request?.filterModel?.revaluation?.values.length === 1) {
              return (
                params.request?.filterModel?.revaluation?.values[0] ===
                'Requested'
              );
            }
          }
          return null;
        };

        const resubmitFilterFn = () => {
          if (params.request?.filterModel?.resubmits?.values) {
            if (params.request?.filterModel?.resubmits?.values.length === 1) {
              return (
                params.request?.filterModel?.resubmits?.values[0] ===
                'Resubmitted'
              );
            }
          }
          return null;
        };

        const sortFn = () => {
          if (params.request?.sortModel?.length > 0) {
            return {
              [params.request?.sortModel[0]?.colId]:
                params.request?.sortModel[0]?.sort.toUpperCase(),
            };
          }

          return { submittedAt: 'DESC' };
        };

        const getDateFilterObj = (dateFilter) => {
          if (!dateFilter) return null;
          if (dateFilter.type === 'inRange') {
            const dateFrom = dateFilter.dateFrom.split(' ')[0];
            const dateTo = dateFilter.dateTo.split(' ')[0];
            const sign = '$between';
            return {
              [sign]: { startDate: dateFrom, endDate: dateTo },
            };
          }
          const date = dateFilter.dateFrom.split(' ')[0];
          let sign = '';
          if (dateFilter.type === 'equals') sign = '$eq';
          else if (dateFilter.type === 'greaterThan') sign = '$gt';
          return {
            [sign]: date,
          };
        };

        const getProjectIdsArray = () => {
          const projectCategoryIdArr = getProjectCategoryFilterList(
            params.request?.filterModel?.category?.values,
            mProjectCategoryList
          );

          const projectIdArr = getProjectFilterList(
            params.request?.filterModel?.project?.values,
            mAllProjectList
          );

          if (projectCategoryIdArr) {
            if (projectIdArr) return [...projectCategoryIdArr, ...projectIdArr];
            else return projectCategoryIdArr;
          } else if (projectIdArr) return projectIdArr;
          else return null;
        };

        const body = {
          filter: {
            firstName: params.request?.filterModel?.name?.filter || null,
            email: params.request?.filterModel?.email?.filter || null,
            adminStatus: params.request?.filterModel?.adminStatus?.values || [
              'pending',
              'on_hold',
              'accepted',
            ],

            cohort: params.request?.filterModel?.cohort?.filter || null,
            evaluatorName:
              (params.request?.filterModel?.evaluator?.type === 'contains'
                ? params.request?.filterModel?.evaluator?.filter
                : null) ||
              params.request?.filterModel?.resubmitEvaluator?.filter ||
              null,
            evaluatorEmail:
              params.request?.filterModel?.evaluatorEmail?.filter ||
              params.request?.filterModel?.resubmitEvaluatorEmail?.filter ||
              null,
            emptyEvaluator:
              params.request?.filterModel?.evaluator?.type === 'noEvaluator' || null,
            evaluationStatus: params.request?.filterModel?.evaluationStatus
              ?.values || ['pending', 'completed', 'rejected'],

            submittedAt: getDateFilterObj(
              params.request?.filterModel?.submittedAt
            ),
            evaluatedAt: getDateFilterObj(
              params.request?.filterModel?.evaluatedAt
            ),

            reEvaluationRequested: reevaluationRequestedFn(),
            reSubmitted: resubmitFilterFn(),
            projectIds: getProjectIdsArray(),
            goalType: mGoalType,
          },
          sort: sortFn(),

          pagination: {
            start: params.request.startRow,
            end: params.request.endRow,
          },
        };

        try {
          getProjectSubmissionList(body).then((response) => {
            params.successCallback(
              response?.data || [],
              response?.pagination?.totalCount || 0
            );
          });
        } catch (error) {
          console.error(error);
          params.failCallback();
        }
      },
    };
  };

  const statusDropdownParams = {
    notAcceptReasonList,
    statusChangeFn: async (studentId, projectId, status, adminFeedback) => {
      const body = {
        studentId: studentId,
        projectId: projectId,
        adminStatus: status,
        adminFeedback,
      };

      try {
        await updateProjectStatus(body);
        reloadData();
      } catch (error) {
        console.error(error);
      }
    },
  };

  const evaluatorDropdownParams = {
    evaluatorList: evaluatorList,
    evaluatorChangeFn: async (studentId, projectId, evaluatorUserId) => {
      const body = {
        studentId,
        projectId,
        evaluatorUserId,
      };

      try {
        await assignEvaluatorInProject(body);
        reloadData();
      } catch (e) {
        console.error(e);
      }
    },
  };

  const reloadData = () => {
    if (gridApi) {
      onGridReady(
        gridApi,
        goalType,
        isApplyDefaultFilter,
        isFirstTimeFilter,
        allProjectList,
        projectCategoryList
      );
    }
  };

  React.useEffect(async () => {
    try {
      const projects = await fetchAllProjects(goalType);
      setAllProjectList(projects);

      const categories = await fetchAllProjectCategories();
      setProjectCategoryList(categories);
      if (gridApi) {
        const filterModel = gridApi.api.getFilterModel();
        delete filterModel.project;
        gridApi.api.setFilterModel(filterModel);

        gridApi.api
          .getFilterInstance('project')
          .setFilterValues(projects?.map((project) => project.name));
      }
    } catch (err) {
      console.error(err);
    }
  }, [goalType]);

  React.useEffect(() => {
    reloadData();
  }, [goalType, refreshCounter, allProjectList, projectCategoryList]);

  React.useEffect(() => {
    if (isApplyDefaultFilter) reloadData();
  }, [isApplyDefaultFilter]);

  // get evaluator list
  React.useEffect(async () => {
    try {
      const response = await getEvaluatorListForProjectSubmission(goalType);
      setEvaluatorList(response);
    } catch (e) {
      console.error(e);
    }
  }, [goalType]);

  React.useEffect(async () => {
    try {
      const response = await getProjectNotAcceptReason();
      setNotAcceptReasonList(response);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const onGridReady = React.useCallback(
    (
      params,
      mGoalType = 'dataScience',
      mIsApplyDefaultFilter = true,
      mIsFirstTimeFilter = true,
      mAllProjectList,
      mProjectCategoryList
    ) => {
      setGridApi(params);

      if (mIsFirstTimeFilter) {
        const defaultFilter = {
          adminStatus: { values: ['pending'], filterType: 'set' },
        };
        params.api.setFilterModel(defaultFilter);
        setIsFirstTimeFilter(false);
      } else if (mIsApplyDefaultFilter) {
        params.api.setFilterModel({});
        setIsApplyDefaultFilter(false);
      }

      const datasource = getServerSideDatasource(
        mGoalType,
        mAllProjectList,
        mProjectCategoryList
      );
      params.api.setServerSideDatasource(datasource);
    },
    []
  );

  return (
    <div className='ag-theme-alpine z-50' style={{ width: '100%' }}>
      <EvaluatorDropdownContext.Provider value={evaluatorDropdownParams}>
        <StatusDropdownContext.Provider value={statusDropdownParams}>
          <AgGridReact
            columnDefs={columnDefs(allProjectList, projectCategoryList)}
            defaultColDef={defaultColDef}
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            onGridReady={onGridReady}
            domLayout='autoHeight'
            rowModelType='serverSide'
            rowHeight={60}
            serverSideStoreType='partial'
            animateRows={true}
            serverSideInfiniteScroll={true}
            pagination={true}
            suppressPaginationPanel={false}
            paginationPageSize={pageSize}
            cacheBlockSize={pageSize}
          />
        </StatusDropdownContext.Provider>
      </EvaluatorDropdownContext.Provider>
    </div>
  );
}

export default ProjectSubmissionTable;
export { StatusDropdownContext };
export { EvaluatorDropdownContext };
